import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowUpToLine, X, CheckCircle2, Trash2, TrendingUp } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminNonDivToDivButton — Round P112 (Feb 2026).
 *
 * Mirror of AdminDivToNonDivButton for the non-dividend universe. Lists
 * every `pays_dividend == False` row that actually has a positive
 * `dividend_yield` — a data-hygiene signal that the row is
 * misclassified and belongs on the dividend side.
 *
 * Per-row actions:
 *   ↑ Arrow (far LEFT)  — route to Pending Dividend Review via
 *                          POST /api/admin/reconcile/non-dividend-with-yield/
 *                                {t}/move-to-dividend-review
 *                          which upserts the ticker into
 *                          `pending_dividend_review` and deletes the
 *                          misclassified non-div `stock_cache` row.
 *   🗑 Trash (far RIGHT) — permanent blocklist via
 *                          POST /api/admin/blocklist/{t}.
 */
export default function AdminNonDivToDivButton({ onStockClick }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [busyTicker, setBusyTicker] = useState(null);
  const [confirmBlocklist, setConfirmBlocklist] = useState(null);

  if (user?.role !== 'admin') return null;

  const fetchList = async () => {
    setLoading(true);
    setReport(null);
    setOpen(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/reconcile/non-dividend-with-yield`,
        { withCredentials: true },
      );
      setReport(data);
    } catch (err) {
      toast.error('Could not load Non-Div → Div reconcile list', {
        description: err?.response?.data?.detail || err?.message,
      });
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const removeFromState = (ticker) => {
    setReport((prev) =>
      prev
        ? {
            ...prev,
            candidates: (prev.candidates || []).filter((c) => c.ticker !== ticker),
            count: Math.max(0, (prev.count || 1) - 1),
          }
        : prev,
    );
  };

  const moveToDividendReview = async (row) => {
    const t = row.ticker;
    setBusyTicker(t);
    try {
      await axios.post(
        `${API}/admin/reconcile/non-dividend-with-yield/${encodeURIComponent(t)}/move-to-dividend-review`,
        {},
        { withCredentials: true },
      );
      toast.success(`${t} → Pending Dividend Review`, {
        description: 'Queued — open the Pending Dividend Review panel to Promote.',
        duration: 4500,
      });
      removeFromState(t);
    } catch (err) {
      toast.error(`Could not move ${t}`, {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusyTicker(null);
    }
  };

  const blocklist = async (row) => {
    const t = row.ticker;
    setBusyTicker(t);
    try {
      const { data } = await axios.post(
        `${API}/admin/blocklist/${encodeURIComponent(t)}`,
        { reason: 'reconcile_non_div_to_div_trash' },
        { withCredentials: true },
      );
      toast.success(`Blocklisted ${t}`, {
        description: `Removed from ${Object.keys(data?.removed || {}).length} collections.`,
        duration: 4000,
      });
      removeFromState(t);
    } catch (err) {
      toast.error(`Could not blocklist ${t}`, {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusyTicker(null);
    }
  };

  const handleTickerClick = (ticker) => {
    if (!ticker) return;
    onStockClick?.(ticker);
  };

  return (
    <>
      <button
        type="button"
        onClick={fetchList}
        disabled={loading}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-teal-500/15 hover:bg-teal-500/25 text-teal-900 dark:text-teal-200 border border-teal-500/40 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        title="Admin: non-dividend rows that actually have a positive yield. Arrow → Pending Dividend Review. Trash → permanent blocklist."
        data-testid="admin-non-div-to-div-fire"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpToLine className="w-4 h-4" />}
        Non-Div → Div
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
          onClick={() => setOpen(false)}
          data-testid="admin-non-div-to-div-modal"
        >
          <div
            className="bg-background border border-teal-500/60 rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-border/60 flex items-center gap-3">
              <ArrowUpToLine className="w-4 h-4 text-teal-600" />
              <div className="flex-1">
                <div className="text-sm font-bold">Non-Div → Div reconciliation</div>
                <div className="text-[10px] text-muted-foreground">
                  Non-dividend rows with a real dividend_yield. Arrow queues to Pending Dividend Review; trash blocklists permanently.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-muted-foreground hover:text-foreground p-1 rounded"
                data-testid="admin-non-div-to-div-close"
                aria-label="Close reconcile modal"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {loading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Scanning non-dividend universe…
                </div>
              )}

              {report && (
                <>
                  <div className="text-[11px] text-muted-foreground" data-testid="admin-non-div-to-div-count">
                    {report.count} candidate{report.count === 1 ? '' : 's'} — non-dividend rows with real yield.
                  </div>

                  {report.count === 0 && (
                    <div className="flex items-center gap-2 px-3 py-4 rounded-md bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-200 text-sm">
                      <CheckCircle2 className="w-4 h-4" />
                      No non-dividend row is carrying a yield. Nothing to reconcile.
                    </div>
                  )}

                  {report.candidates && report.candidates.length > 0 && (
                    <div className="border border-border/60 rounded-md overflow-hidden">
                      <div className="divide-y divide-border/40">
                        {report.candidates.map((row) => (
                          <div
                            key={row.ticker}
                            className="px-3 py-1.5 flex items-center gap-3 text-xs"
                            data-testid={`admin-non-div-to-div-row-${row.ticker}`}
                          >
                            <button
                              type="button"
                              onClick={() => moveToDividendReview(row)}
                              disabled={busyTicker === row.ticker}
                              className="text-muted-foreground hover:text-emerald-600 disabled:opacity-40 shrink-0"
                              title={`Move ${row.ticker} to the Pending Dividend Review queue.`}
                              data-testid={`admin-non-div-to-div-move-${row.ticker}`}
                            >
                              {busyTicker === row.ticker
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <TrendingUp className="w-3.5 h-3.5" />}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleTickerClick(row.ticker)}
                              className="font-mono font-bold w-14 shrink-0 text-teal-700 dark:text-teal-300 hover:text-teal-500 dark:hover:text-teal-200 underline-offset-2 hover:underline text-left"
                              title={`Open ${row.ticker} in the Stock Modal`}
                              data-testid={`admin-non-div-to-div-ticker-${row.ticker}`}
                            >
                              {row.ticker}
                            </button>
                            <span className="flex-1 truncate text-muted-foreground" title={row.name}>
                              {row.name || '—'}
                            </span>
                            <span className="font-mono text-[10px] text-muted-foreground shrink-0">
                              y={row.dividend_yield ?? '—'} · r={row.dividend_rate ?? '—'}
                            </span>
                            <button
                              type="button"
                              onClick={() => setConfirmBlocklist(row)}
                              disabled={busyTicker === row.ticker}
                              className="text-muted-foreground hover:text-rose-600 disabled:opacity-40 shrink-0"
                              title={`Permanently blocklist ${row.ticker}.`}
                              data-testid={`admin-non-div-to-div-trash-${row.ticker}`}
                            >
                              {busyTicker === row.ticker
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <Trash2 className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="text-[10px] text-muted-foreground pt-2 border-t border-border/40">
                    Audited at {report.audited_at} · list capped at 1000.
                  </div>
                </>
              )}
            </div>

            <div className="px-4 py-2 border-t border-border/60 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={fetchList}
                disabled={loading}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-60"
                data-testid="admin-non-div-to-div-refresh"
              >
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowUpToLine className="w-3 h-3" />}
                Refresh
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-[11px] px-2.5 py-1 rounded-md border border-border hover:bg-muted"
                data-testid="admin-non-div-to-div-dismiss"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmBlocklist && (
        <div
          className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setConfirmBlocklist(null)}
          data-testid="admin-non-div-to-div-blocklist-confirm-modal"
        >
          <div
            className="bg-background border border-rose-500/60 rounded-lg shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-border/60 flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-rose-600" />
              <div className="text-sm font-bold">Permanently blocklist ticker?</div>
            </div>
            <div className="px-5 py-4 text-[12px] text-muted-foreground space-y-2">
              <div>
                <span className="font-mono font-bold text-foreground">{confirmBlocklist.ticker}</span>
                {' — '}
                <span>{confirmBlocklist.name}</span>
              </div>
              <p className="pt-2 text-rose-700 dark:text-rose-400 font-semibold">
                Permanent. Use only for tickers that clearly don&apos;t belong.
              </p>
            </div>
            <div className="px-5 py-3 border-t border-border/60 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmBlocklist(null)}
                className="px-3 py-1.5 rounded-md text-[12px] font-bold border border-border hover:bg-muted"
                data-testid="admin-non-div-to-div-blocklist-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const row = confirmBlocklist;
                  setConfirmBlocklist(null);
                  blocklist(row);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-bold bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="admin-non-div-to-div-blocklist-confirm"
              >
                <Trash2 className="w-3 h-3" />
                Remove and blocklist
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
